<script setup lang="ts">
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isNil } from 'lodash';
import {
  DEFAULT_ID_VALIDATION_DATA_RETENTION_DAYS,
  getTranslatedSettings,
  ID_VALIDATION_DATA_RETENTION_DAYS_LIMITS
} from '@nova/core';

const { t } = useI18n();

const { min: MIN_DAYS, max: MAX_DAYS } = ID_VALIDATION_DATA_RETENTION_DAYS_LIMITS;

type IdValidationDataRetentionSettingsModel = {
  autoDeleteIdValidationData: boolean;
  idValidationDataRetentionDays: number | null;
};

const { entitySettings, readOnly = false } = defineProps<{
  entitySettings: IdValidationDataRetentionSettingsModel;
  readOnly?: boolean;
}>();

const localSettings = computed(() => entitySettings);

const autoDeleteSetting = computed(() => {
  const setting = getTranslatedSettings('Warehouse').autoDeleteIdValidationData;
  return {
    label: setting?.label ?? '',
    description: setting?.description ?? ''
  };
});

const isAutoDeleteEnabled = computed(() => localSettings.value.autoDeleteIdValidationData);

function onDaysChange(value: string) {
  localSettings.value.idValidationDataRetentionDays = value === '' ? null : Number(value);
}

function daysRule(value: unknown) {
  if (!isAutoDeleteEnabled.value) {
    return true;
  }
  const days = Number(value);
  if (value === '' || value === null || !Number.isInteger(days)) {
    return t('warehouse.pages.settings.expansionPanels.idValidation.dataRetention.daysRequired');
  }
  if (days < MIN_DAYS || days > MAX_DAYS) {
    return t('warehouse.pages.settings.expansionPanels.idValidation.dataRetention.daysOutOfRange', {
      minValue: MIN_DAYS,
      maxValue: MAX_DAYS
    });
  }
  return true;
}

watch(isAutoDeleteEnabled, enabled => {
  if (enabled && isNil(localSettings.value.idValidationDataRetentionDays)) {
    localSettings.value.idValidationDataRetentionDays = DEFAULT_ID_VALIDATION_DATA_RETENTION_DAYS;
  }
});
</script>

<template>
  <div class="d-flex flex-column my-4">
    <span class="typography-body-sm-bold text-medium-emphasis mb-2">
      {{ t('core.settings.sectionLabels.dataRetention') }}
    </span>

    <v-checkbox
      v-model="localSettings.autoDeleteIdValidationData"
      color="primary"
      density="compact"
      hide-details
      :disabled="readOnly"
      :label="autoDeleteSetting.label"
      data-testid="auto-delete-id-validation-data" />

    <div class="d-flex align-center gap-2 mt-4">
      <span class="typography-body-md" :class="{ 'text-disabled': !isAutoDeleteEnabled }">
        {{ t('warehouse.pages.settings.expansionPanels.idValidation.dataRetention.deleteAfter') }}
      </span>
      <v-text-field
        class="retention-days-input flex-grow-0"
        type="number"
        variant="outlined"
        density="compact"
        hide-details="auto"
        :model-value="localSettings.idValidationDataRetentionDays"
        :disabled="readOnly || !isAutoDeleteEnabled"
        :min="MIN_DAYS"
        :max="MAX_DAYS"
        :rules="[daysRule]"
        data-testid="id-validation-data-retention-days"
        @update:model-value="onDaysChange" />
      <span class="typography-body-md" :class="{ 'text-disabled': !isAutoDeleteEnabled }">
        {{ t('warehouse.pages.settings.expansionPanels.idValidation.dataRetention.days') }}
      </span>
    </div>

    <p class="typography-body-sm text-medium-emphasis mt-2">
      {{ autoDeleteSetting.description }}
    </p>
  </div>
</template>

<style scoped lang="scss">
.retention-days-input {
  max-width: 6rem;
}
</style>
